import React from 'react';
import { FileDown, FileUp, ShieldAlert, Trash2 } from 'lucide-react';
import { Button } from '../../ui/Button/Button';
import { Chip } from '../ui/Chip';
import { PanelSection } from '../ui/PanelSection';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { TypedConfirmDialog } from '../ui/TypedConfirmDialog';
import { EmptyNote } from '../ui/EmptyNote';
import { EXPORT_FILE } from '../../../lib/export/catalog';
import { buildExport, readExport } from '../../../lib/export/index';
import { configCounts, configFromRows, configRows } from '../../../lib/export/rcu';
import type { ConfigCounts } from '../../../lib/export/rcu';
import { EXPORT_ISSUE, blocking, exportMessage } from '../../../lib/export/issue';
import type { ExportIssue } from '../../../lib/export/issue';
import { safeFileName, saveText, sizeOf, readTextFile } from '../../../lib/file';
import { TONE } from '../../../lib/rcu/catalog/feedback';
import { SEVERITY, issueMessage, severityOf } from '../../../lib/rcu/catalog/issue';
import { validateConfig } from '../../../lib/rcu/validation/validate';
import type { RcuConfig } from '../../../lib/rcu/model';
import { useLoadStateStore } from '../../../stores/loadStateStore';
import { useRcuConfigStore } from '../../../stores/rcuConfigStore';
import { useRcuLinkStore } from '../../../stores/rcuLinkStore';
import { useKeypadStore } from '../../../features/keypads';
import { keypadTargets } from '../keypadTargets';
import { bookCounts, keypadBookFrom, keypadRows } from '../keypadTransfer';
import type { BookCounts, KeypadBook } from '../keypadTransfer';
import { useToastStore } from '../../../stores/toastStore';

/**
 * Moving a tablet's configuration in and out as one CSV file.
 *
 * This is a commissioning tool, so it is written for the two people who use it:
 * a technician cloning a room's setup onto the next tablet, and an engineer who
 * would rather diff two installations in a spreadsheet than in a browser. Both
 * want the same three things — everything in the file, nothing applied until it
 * has been read, and a plain statement of what is *not* in it.
 *
 * Nothing is applied on choosing a file. The file is read into a plan, the plan
 * is shown, and only a second, confirmed action commits it.
 */

/** What a file holds, read but not yet applied. */
interface ImportPlan {
  readonly fileName: string;
  readonly config: RcuConfig;
  readonly book: KeypadBook;
  readonly configCounts: ConfigCounts;
  readonly bookCounts: BookCounts;
  /** Records the decoders refused, plus ids that repeated. */
  readonly dropped: number;
  /** Warnings the console would report about the imported configuration. */
  readonly warnings: readonly string[];
  /**
   * Errors the console would report about it. A file with any of these cannot be
   * applied, and the panel says so before offering to try rather than after.
   */
  readonly errors: readonly string[];
  /** Key mappings naming an output the imported configuration does not offer. */
  readonly orphanMappings: number;
}

/**
 * What a technician types to clear a tablet that has no device ID yet.
 *
 * The console's other irreversible commands ask for the RCU's serial number,
 * which is written on the hardware. A tablet that has never been configured has
 * no such thing to ask for, and asking for nothing would make the confirmation
 * a plain button.
 */
const CLEAR_CONFIRMATION = 'clear';

/** The filename for a download, from whatever identity the tablet has. */
const fileNameFor = (config: RcuConfig, at: string): string => {
  const identity =
    config.rcu.deviceId.trim() || config.rcu.serialNumber.trim() || EXPORT_FILE.FALLBACK_NAME;
  const date = at.slice(0, 'YYYY-MM-DD'.length);
  const name = safeFileName(identity, EXPORT_FILE.FALLBACK_NAME, EXPORT_FILE.SAFE_NAME);
  return `${EXPORT_FILE.PREFIX}-${name}-${date}.${EXPORT_FILE.EXTENSION}`;
};

/** How many key mappings name an output this configuration does not offer. */
const orphanMappingCount = (config: RcuConfig, book: KeypadBook): number => {
  const outputs = new Set(keypadTargets(config).map((target) => target.id));
  return book.keypads.reduce(
    (total, keypad) =>
      total +
      keypad.keys.reduce(
        (keyTotal, key) =>
          keyTotal +
          key.states.reduce(
            (stateTotal, mapping) =>
              stateTotal +
              mapping.loads.filter((load) => !outputs.has(load.targetId)).length,
            0,
          ),
        0,
      ),
    0,
  );
};

/** A count and its noun, singular or plural, so a sentence reads as one. */
const plural = (count: number, singular: string, many: string): string =>
  `${count} ${count === 1 ? singular : many}`;

/**
 * A refusal from the configuration store, in the shape this panel shows.
 *
 * The store speaks the configuration's issue vocabulary and this panel speaks
 * the file's; the one place they meet is here, so a refused import reads as one
 * kind of message to the technician whichever layer refused it.
 */
const refusalFor = (message: string): ExportIssue => blocking(EXPORT_ISSUE.CONFIG_INVALID, message);

const Stat: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div className="rounded-xl bg-[#FAFAFA] px-3 py-2">
    <p className="text-xs uppercase tracking-wider text-[#999999]">{label}</p>
    <p className="font-display text-xl text-[#333333]">{value}</p>
  </div>
);

/** A list of issues, each as its own line. */
const IssueLines: React.FC<{ issues: readonly ExportIssue[] }> = ({ issues }) => (
  <ul className="space-y-1.5">
    {issues.map((issue) => (
      <li key={issue.message} className="flex items-start gap-2 text-sm text-[#C62828]">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
        {exportMessage(issue)}
      </li>
    ))}
  </ul>
);

/**
 * Everything that has to be let go of when the installation itself changes.
 *
 * The configuration and the keypad book are only half of what this tablet holds.
 * The rest is *derived* — readings the device reported, what it answered about
 * mappings, and the guest's own last touch on a control — and every piece of it
 * is keyed by something the old installation defined: a relay number, a trigger,
 * a load id. Left alone, an imported room inherits the previous room's readings
 * and shows a control where a guest in another room left it.
 *
 * `LinkHost` re-reads the device when the rails change, so the readings come
 * back on their own; what must not survive is the stale answer in the meantime.
 */
const useForgetDerivedState = (): (() => void) => {
  const clearLive = useRcuLinkStore((state) => state.clearLive);
  const forgetAnswers = useKeypadStore((state) => state.forgetAnswers);
  const clearLoads = useLoadStateStore((state) => state.clear);

  return React.useCallback(() => {
    clearLive();
    forgetAnswers();
    clearLoads();
  }, [clearLive, forgetAnswers, clearLoads]);
};

/**
 * The counts the clear section describes.
 *
 * Optional and overridable so the panel can be rendered against a known
 * installation: it otherwise reads the stores, which a server render cannot seed.
 */
export interface TransferPanelProps {
  readonly summary?: {
    readonly rails: number;
    readonly bridges: number;
    readonly loads: number;
    readonly rooms: number;
    readonly panels: number;
  };
}

/** Import and export, on one panel. */
export const TransferPanel: React.FC<TransferPanelProps> = ({ summary }) => {
  const config = useRcuConfigStore((state) => state.config);
  const applyConfig = useRcuConfigStore((state) => state.applyConfig);
  const resetConfig = useRcuConfigStore((state) => state.resetConfig);
  const book = useKeypadStore((state) => state.book);
  const replaceBook = useKeypadStore((state) => state.replaceBook);
  const clearBook = useKeypadStore((state) => state.clearBook);
  const success = useToastStore((state) => state.success);
  const error = useToastStore((state) => state.error);
  const forgetDerived = useForgetDerivedState();

  const [plan, setPlan] = React.useState<ImportPlan | null>(null);
  const [refusals, setRefusals] = React.useState<readonly ExportIssue[]>([]);
  const [reading, setReading] = React.useState(false);
  const [confirmingClear, setConfirmingClear] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const counts = configCounts(config);
  const keys = bookCounts(book);
  const exportedAt = new Date().toISOString();

  /** What the clear section describes, from the stores unless told otherwise. */
  const clearing = summary ?? {
    rails: counts.rails,
    bridges: counts.bridges,
    loads: counts.loads,
    rooms: counts.rooms,
    panels: keys.keypads,
  };

  /** Nothing worth clearing: the button is offered only when there is. */
  const isEmpty =
    clearing.rails === 0 &&
    clearing.bridges === 0 &&
    clearing.loads === 0 &&
    clearing.rooms === 0 &&
    clearing.panels === 0;

  /**
   * What the technician types back to clear the tablet.
   *
   * The device ID when there is one, because it is written on the hardware. On a
   * tablet that has never been configured there is nothing to confirm against, so
   * the word itself is the confirmation.
   */
  const expectedId = config.rcu.deviceId.trim() === '' ? CLEAR_CONFIRMATION : config.rcu.deviceId.trim();

  const handleExport = (): void => {
    const text = buildExport({
      sections: [configRows(config), keypadRows(book)],
      exportedAt,
    });
    saveText(text, fileNameFor(config, exportedAt), EXPORT_FILE.MIME_TYPE);
    success(
      `Exported ${plural(counts.loads, 'load', 'loads')} and ${plural(keys.keypads, 'panel', 'panels')}.`,
    );
  };

  /**
   * Reads a chosen file into a plan, or says why it will not be used.
   *
   * A refusal is reported twice on purpose: on the panel, where it stays for as
   * long as the technician is looking at it, and as a toast, because the panel's
   * copy is easy to miss when the file dialog has just closed over it.
   */
  const handleFile = (file: File): void => {
    setReading(true);
    setRefusals([]);
    setPlan(null);

    /** Shows a refusal in both places, and stops the read. */
    const refuse = (issue: ExportIssue): void => {
      setRefusals([issue]);
      error(exportMessage(issue));
    };

    void readTextFile(file)
      .then((text) => {
        const read = readExport(text, sizeOf(file));
        if (!read.ok) {
          refuse(read.issue);
          return;
        }

        const now = new Date().toISOString();
        const decodedConfig = configFromRows(read.value.document, now);
        if (!decodedConfig.ok) {
          refuse(decodedConfig.issue);
          return;
        }

        const decodedBook = keypadBookFrom(read.value.document, now);
        if (!decodedBook.ok) {
          refuse(decodedBook.issue);
          return;
        }

        const { config: next, droppedRecords: droppedConfig } = decodedConfig.value;
        const { book: nextBook, droppedRecords: droppedBook } = decodedBook.value;

        setPlan({
          fileName: file.name,
          config: next,
          book: nextBook,
          configCounts: configCounts(next),
          bookCounts: bookCounts(nextBook),
          dropped: droppedConfig + droppedBook,
          warnings: validateConfig(next)
            .filter((issue) => severityOf(issue) !== SEVERITY.ERROR)
            .map(issueMessage),
          errors: validateConfig(next)
            .filter((issue) => severityOf(issue) === SEVERITY.ERROR)
            .map(issueMessage),
          orphanMappings: orphanMappingCount(next, nextBook),
        });
      })
      .catch((cause: unknown) => {
        refuse(refusalFor(`That file could not be read: ${String(cause)}`));
      })
      .finally(() => setReading(false));
  };

  /**
   * Applies the file, then lets go of everything derived from the old one.
   *
   * The order matters: the derived state is dropped only once the new
   * configuration is committed, so a refusal leaves the tablet exactly as it was
   * rather than half-forgotten.
   */
  const handleConfirm = (): void => {
    if (plan === null) return;

    const applied = applyConfig(plan.config);
    if (!applied.ok) {
      setRefusals([refusalFor(issueMessage(applied.issue))]);
      setPlan(null);
      error(issueMessage(applied.issue));
      return;
    }

    replaceBook(plan.book);
    forgetDerived();
    success(
      `Imported ${plural(plan.configCounts.rails, 'rail', 'rails')}, ${plural(
        plan.configCounts.loads,
        'load',
        'loads',
      )} and ${plural(plan.bookCounts.keypads, 'panel', 'panels')} from ${plan.fileName}.`,
    );
    setPlan(null);
    clearChoice();
  };

  /** Empties the tablet, storage included, and drops everything derived from it. */
  const handleClear = (): void => {
    resetConfig();
    clearBook();
    forgetDerived();
    setConfirmingClear(false);
    clearChoice();
    success('All data cleared. This tablet has no configuration and no keypad panels.');
  };

  const clearChoice = (): void => {
    setPlan(null);
    setRefusals([]);
    if (inputRef.current !== null) inputRef.current.value = '';
  };

  return (
    <div className="space-y-4">
      <PanelSection
        title="This tablet"
        description="What is stored here now. An import replaces all of it."
        action={
          <Button variant="primary" size="sm" onClick={handleExport}>
            <FileDown className="mr-2 h-3.5 w-3.5" />
            Download CSV
          </Button>
        }
      >
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Rails" value={counts.rails} />
          <Stat label="Bridges" value={counts.bridges} />
          <Stat label="Loads" value={counts.loads} />
          <Stat label="Rooms" value={counts.rooms} />
          <Stat label="Panels" value={keys.keypads} />
          <Stat label="Keys" value={keys.keys} />
          <Stat label="Mappings" value={keys.mappings} />
          <Stat label="Written" value={keys.written} />
        </div>
        <p className="mt-3 text-xs text-[#666666]">
          The file is named after the device ID, or the serial number when there is none:{' '}
          <code>{fileNameFor(config, exportedAt)}</code>. Everything above is written into it,
          including each load&rsquo;s wiring and every keypad key mapping.
        </p>
      </PanelSection>

      <PanelSection
        title="Import"
        description="Read a file written by this console. Nothing changes until you confirm."
        action={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={reading}
          >
            <FileUp className="mr-2 h-3.5 w-3.5" />
            {reading ? 'Reading…' : 'Choose file'}
          </Button>
        }
      >
        <input
          ref={inputRef}
          type="file"
          accept={`.${EXPORT_FILE.EXTENSION},${EXPORT_FILE.MIME_TYPE}`}
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file !== undefined) handleFile(file);
          }}
        />

        {refusals.length > 0 && <IssueLines issues={refusals} />}

        {refusals.length === 0 && plan === null && (
          <EmptyNote message="No file chosen. A file this console did not write is refused, not guessed at." />
        )}

        {plan !== null && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Chip label={plan.fileName} tone={TONE.INFO} />
              <span className="text-sm text-[#666666]">
                {plural(plan.configCounts.rails, 'rail', 'rails')} ·{' '}
                {plural(plan.configCounts.loads, 'load', 'loads')} ·{' '}
                {plural(plan.configCounts.rooms, 'room', 'rooms')} ·{' '}
                {plural(plan.bookCounts.keypads, 'panel', 'panels')} ·{' '}
                {plural(plan.bookCounts.mappings, 'mapping', 'mappings')}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Rails" value={plan.configCounts.rails} />
              <Stat label="Bridges" value={plan.configCounts.bridges} />
              <Stat label="Loads" value={plan.configCounts.loads} />
              <Stat label="Wired" value={plan.configCounts.wired} />
              <Stat label="Rooms" value={plan.configCounts.rooms} />
              <Stat label="Panels" value={plan.bookCounts.keypads} />
              <Stat label="Mappings" value={plan.bookCounts.mappings} />
              <Stat label="Written" value={plan.bookCounts.written} />
            </div>

            {plan.dropped > 0 && (
              <p className="text-sm text-[#B45309]">
                {plural(plan.dropped, 'record was', 'records were')} unreadable and left out.
              </p>
            )}

            {plan.orphanMappings > 0 && (
              <p className="text-sm text-[#B45309]">
                {plural(plan.orphanMappings, 'key mapping names', 'key mappings name')} an output
                this configuration does not offer. They import as they are and are flagged in the
                Keypads tab.
              </p>
            )}

            {plan.warnings.length > 0 && (
              <div>
                <p className="mb-1.5 text-sm font-medium text-[#333333]">
                  The console would flag this configuration:
                </p>
                <ul className="space-y-1 text-sm text-[#666666]">
                  {plan.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}

            {plan.errors.length > 0 && (
              <div className="rounded-xl bg-[#F44336]/8 px-4 py-3">
                <p className="mb-1.5 text-sm font-medium text-[#C62828]">
                  This configuration cannot be applied:
                </p>
                <ul className="space-y-1 text-sm text-[#C62828]">
                  {plan.errors.map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 border-t border-[#E0E0E0] pt-4">
              <Button
                variant="primary"
                size="sm"
                onClick={handleConfirm}
                disabled={plan.errors.length > 0}
              >
                Replace this tablet
              </Button>
              <Button variant="secondary" size="sm" onClick={clearChoice}>
                Discard
              </Button>
              <span className="text-xs text-[#666666]">
                {plan.errors.length > 0
                  ? 'Fix the file, or edit it by hand, then choose it again.'
                  : 'Rails, bridges, loads, rooms and every keypad panel are replaced.'}
              </span>
            </div>
          </div>
        )}
      </PanelSection>

      <PanelSection
        title="Clear all data"
        description="Empties this tablet, for decommissioning it or starting a room again from nothing."
        action={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setConfirmingClear(true)}
            disabled={isEmpty}
            className={isEmpty ? '' : 'border-[#F44336] text-[#C62828] hover:border-[#C62828]'}
          >
            <Trash2 className="mr-2 h-3.5 w-3.5" />
            Clear all data
          </Button>
        }
      >
        <p className="text-sm text-[#666666]">
          {isEmpty
            ? 'This tablet holds nothing: no configuration and no keypad panels.'
            : `Removes ${plural(clearing.rails, 'rail', 'rails')}, ${plural(
                clearing.bridges,
                'bridge',
                'bridges',
              )}, ${plural(clearing.loads, 'load', 'loads')}, ${plural(
                clearing.rooms,
                'room',
                'rooms',
              )} and ${plural(clearing.panels, 'keypad panel', 'keypad panels')}, and the device
              readings and keypad answers held beside them. The broker settings and the console PIN
              are kept, so the tablet stays reachable.`}
        </p>
        <p className="mt-3 text-xs text-[#666666]">
          This cannot be undone. Download a copy first if there is any chance the configuration is
          wanted again — there is no undo, and the device itself is not touched.
        </p>
      </PanelSection>

      <PanelSection
        title="What the file does not hold"
        description="Read this before relying on a file as a backup."
      >
        <ul className="space-y-2 text-sm text-[#666666]">
          <li>
            <span className="font-medium text-[#333333]">Broker settings.</span> The address,
            username and password for the MQTT link are credentials, and a CSV copied over USB or
            emailed is not where they belong. Set them again on the new tablet.
          </li>
          <li>
            <span className="font-medium text-[#333333]">The console PIN.</span> It is device-local
            on purpose; a new tablet starts at the factory default.
          </li>
          <li>
            <span className="font-medium text-[#333333]">Live device state.</span> What the RCU
            reports — relay positions, dimmer levels, firmware version — belongs to the hardware,
            not the tablet. Press <span className="font-medium">Read</span> on the device tabs
            after importing.
          </li>
          <li>
            <span className="font-medium text-[#333333]">What was written to the RCU.</span> The
            file carries this tablet&rsquo;s record of the mappings it delivered. That is a claim
            about the tablet, not about the device, so a mapping is only known to be on the
            hardware once it has been read back.
          </li>
        </ul>
      </PanelSection>

      <ConfirmDialog
        isOpen={plan !== null}
        title="Replace this tablet's configuration"
        message={
          plan === null
            ? ''
            : `This replaces every rail, bridge, load and room on this tablet, and all ${plural(
                plan.bookCounts.keypads,
                'keypad panel',
                'keypad panels',
              )} with what ${plan.fileName} holds. What is here now is not kept.`
        }
        confirmLabel="Replace everything"
        destructive
        onCancel={clearChoice}
        onConfirm={handleConfirm}
      />

      {/*
        Typing the device ID back, the way the console's other irreversible
        commands do. A tap is too cheap for something with no undo, and the id is
        written on the hardware, so it is the one thing a technician clearing a
        tablet by mistake is unlikely to have to hand.
      */}
      <TypedConfirmDialog
        isOpen={confirmingClear}
        title="Clear all data"
        message={`This removes the configuration and every keypad panel from this tablet, and cannot be undone. The RCU itself is not touched. Type ${expectedId} to confirm.`}
        expected={expectedId}
        confirmLabel="Clear everything"
        onCancel={() => setConfirmingClear(false)}
        onConfirm={handleClear}
      />
    </div>
  );
};

export default TransferPanel;
